
import { defineComponent, ref } from "vue";
import _ from "lodash";
import FormInput from "@/components/form/inputs/FormInput.vue";
import DefaultButton from "@/components/ui/DefaultButton.vue";
import Modal from "@/components/ui/Modal.vue";
import Spinner from "@/components/ui/Spinner.vue";
import { FormElementData } from "@/models/FormElementData";
import { FormElementState } from "@/models/FormElementState";
import {
  PossibleValues,
  SelectOption,
  Values,
  prepareOptionsStringId,
} from "@/common/FormTools";
import { filtersData, prepareFilterParams } from "@/common/FilterTools";
import { decodeMonth, monthOptions } from "@/common/ReportMonths";
import performanceReportService, {
  PerformanceReportParams,
} from "@/services/performance-report.service";

export type PerformanceReportExportMethods = {
  monthChange: (fieldId: string, newValue: PossibleValues) => void;
  buildCarrierElementData: () => FormElementData;
  setTrackingData: (hasData: boolean) => void;
  buildCountryElementData: () => FormElementData;
  filterChange: (fieldId: string, newValue: PossibleValues) => void;
  downloadPdf: () => Promise<void>;
  downloadXlsx: () => Promise<void>;
  buildParams: () => PerformanceReportParams;
  closeModal: () => void;
};

export type PerformanceReportExportState = {
  isDownloading: boolean;
  showModal: boolean;
  modalMessage: string;
  month: string;
  monthElementData: FormElementData;
  monthInitialState: FormElementState;
  carrierElementData: FormElementData;
  countryElementData: FormElementData;
  filtersValue: Values;
  // Undefined until the carrier list has been fetched, so the warning is not
  // shown while we do not yet know either way.
  carriersLoaded: boolean;
  hasTrackingData: boolean;
};

export default defineComponent({
  name: "PerformanceReportExport",
  components: {
    FormInput,
    DefaultButton,
    Modal,
    Spinner,
  },
  setup() {
    const methods = {} as PerformanceReportExportMethods;
    const options = monthOptions();

    const state = ref<PerformanceReportExportState>({
      isDownloading: false,
      showModal: false,
      modalMessage: "",
      // Defaults to the most recent closed month, which is what gets sent to
      // customers at the start of each month.
      month: options[0].value as string,
      monthElementData: {
        id: "month",
        type: "select",
        label: "report.performance.month",
        options: () => options,
        disabledEmptyOption: true,
        class: "flex flex-col",
      },
      monthInitialState: { value: options[0].value },
      carrierElementData: filtersData.suppliers as FormElementData,
      countryElementData: filtersData.countries as FormElementData,
      filtersValue: {},
      carriersLoaded: false,
      hasTrackingData: true,
    });

    // Carriers are re-read per period: one with tracking data in June may have
    // none in July, and offering it would produce a report of zeroes.
    methods.monthChange = (fieldId: string, newValue: PossibleValues) => {
      if (!newValue) return;

      state.value.month = newValue as string;
      state.value.filtersValue = {
        ...state.value.filtersValue,
        suppliers: [],
        countries: [],
      };
      state.value.carriersLoaded = false;
      state.value.carrierElementData = methods.buildCarrierElementData();
    };

    methods.buildCarrierElementData = (): FormElementData => ({
      ...filtersData.suppliers,
      options: async () => {
        const { dateFrom, dateTo } = decodeMonth(state.value.month);
        const response = await performanceReportService.carriers(
          dateFrom,
          dateTo
        );
        const suppliers = response.data.suppliers ?? [];

        // No carrier with tracking data means there is nothing to report on for
        // this customer and month - say so rather than letting them download a
        // report of zeroes, which reads as total delivery failure.
        methods.setTrackingData(suppliers.length > 0);

        return prepareOptionsStringId(suppliers);
      },
      // The options depend on the month, not on the other filters.
      affectedBy: [],
    });

    methods.setTrackingData = (hasData: boolean) => {
      state.value.carriersLoaded = true;
      state.value.hasTrackingData = hasData;
      state.value.countryElementData = methods.buildCountryElementData();
    };

    // The multi-select greys itself out when it has no options, so withholding
    // them is what disables the field - it does not read a disabled flag.
    methods.buildCountryElementData = (): FormElementData => ({
      ...(filtersData.countries as FormElementData),
      options: async (values: Values) => {
        if (!state.value.hasTrackingData) return [];

        const source = filtersData.countries.options as (
          values: Values
        ) => Promise<SelectOption[]>;

        return source(values);
      },
    });

    methods.filterChange = (fieldId: string, newValue: PossibleValues) => {
      state.value.filtersValue = {
        ...state.value.filtersValue,
        [fieldId]: newValue,
      };
    };

    methods.buildParams = (): PerformanceReportParams => {
      const { dateFrom, dateTo } = decodeMonth(state.value.month);

      return {
        date_from: dateFrom,
        date_to: dateTo,
        ..._.pickBy(prepareFilterParams(state.value.filtersValue)),
      };
    };

    const runDownload = async (
      download: (params: PerformanceReportParams) => Promise<unknown>
    ): Promise<void> => {
      state.value.isDownloading = true;
      state.value.showModal = false;

      return download(methods.buildParams())
        .then(() => undefined)
        .catch((error) => {
          state.value.modalMessage =
            error?.response?.status === 400
              ? "report.performance.modal.too_many_parcels"
              : "report.performance.modal.general_error";
          state.value.showModal = true;
        })
        .finally(() => {
          state.value.isDownloading = false;
        });
    };

    methods.downloadPdf = (): Promise<void> =>
      runDownload(performanceReportService.downloadPdf);

    methods.downloadXlsx = (): Promise<void> =>
      runDownload(performanceReportService.downloadXlsx);

    methods.closeModal = () => {
      state.value.showModal = false;
    };

    state.value.carrierElementData = methods.buildCarrierElementData();

    return {
      state,
      methods,
      filtersData,
    };
  },
});
